import {
  arrayPrototype,
  getPrototypeOf,
  keys,
  mapPrototype,
  objectPrototype,
  setPrototype,
  toString
} from '../shortcut'
import { isPrivateKey } from './isPrivateKey'

import type { TWeakMap } from '../types'

interface IsEqual {
  (a?: any, b?: any): boolean
  seen?: TWeakMap | undefined
}

function internalIsEqual(a?: any, b?: any): boolean {
  if (a === b) return true
  const typeA = typeof a
  const typeB = typeof b
  if (typeA !== typeB || a == null || b == null) return false
  if (typeA !== 'object') return Object.is(a, b)
  if (isEqual.seen?.has(a)) return isEqual.seen.get(a) === b
  isEqual.seen?.set(a, b)
  const protoA = getPrototypeOf(a)
  const protoB = getPrototypeOf(b)
  if (protoA !== protoB) return false
  if (protoA === arrayPrototype) {
    if (a.length !== b.length) return false
    for (const [i, element] of a.entries()) {
      if (!internalIsEqual(element, b[i])) return false
    }
    return true
  }
  if (protoA === objectPrototype || toString.call(a) === '[object Object]') {
    const visited: string[] = []
    for (const key of keys(a)) {
      if (isPrivateKey(key)) continue
      if (!internalIsEqual(a[key], b[key])) return false
      visited.push(key)
    }
    for (const key of keys(b)) {
      if (visited.includes(key) || isPrivateKey(key)) continue
      if (!internalIsEqual(a[key], b[key])) return false
    }
    return true
  }
  if (protoA === mapPrototype) {
    if (a.size !== b.size) return false
    for (const [key, value] of a) {
      if (!b.has(key) || !internalIsEqual(value, b.get(key))) return false
    }
    return true
  }
  if (protoA === setPrototype) {
    if (a.size !== b.size) return false
    const valuesA = Array.from(a)
    const valuesB = Array.from(b)
    for (const [i, element] of valuesA.entries()) {
      if (!internalIsEqual(element, valuesB[i])) return false
    }
    return true
  }
  return false
}

export const isEqual: IsEqual = (a, b) => {
  if (a === b) return true
  const typeA = typeof a
  const typeB = typeof b
  if (typeA !== typeB || a == null || b == null) return false
  if (typeA !== 'object') return Object.is(a, b)

  const prevSeen = isEqual.seen
  isEqual.seen = prevSeen ?? new WeakMap()
  try {
    return internalIsEqual(a, b)
  } finally {
    isEqual.seen = prevSeen
  }
}
